/**
 * StatsCard Component
 *
 * Displays a single statistic with icon, value, and label
 */

import React from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { TrendDirection } from '../../types';
import { TrendIndicator } from '../shared/TrendIndicator';

interface StatsCardProps {
  /** Icon to display */
  icon: React.ReactNode;
  /** Label for the stat */
  label: string;
  /** Main value to display */
  value: string | number | null | undefined;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Optional trend direction */
  trend?: TrendDirection | string | null;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Color variant for the icon */
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /** Value suffix (e.g., "%" or "notes") */
  suffix?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  icon,
  label,
  value,
  subtitle,
  trend,
  isLoading = false,
  color = 'primary',
  suffix,
}) => {
  const theme = useTheme();

  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  const bgColorMap = {
    primary: theme.palette.primary.light + '20',
    success: theme.palette.success.light + '20',
    warning: theme.palette.warning.light + '20',
    error: theme.palette.error.light + '20',
    info: theme.palette.info.light + '20',
  };

  const displayValue = value === null || value === undefined ? '—' : value;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Icon and Label Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: bgColorMap[color],
            color: colorMap[color],
            '& svg': {
              fontSize: 20,
            },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Value */}
      {isLoading ? (
        <Skeleton width="60%" height={40} />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              lineHeight: 1.2,
            }}
          >
            {displayValue}
          </Typography>
          {suffix && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              {suffix}
            </Typography>
          )}
        </Box>
      )}

      {/* Subtitle and Trend */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 'auto',
          pt: 1,
        }}
      >
        {subtitle && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {subtitle}
          </Typography>
        )}
        {trend && <TrendIndicator trend={trend} showLabel size="small" />}
      </Box>
    </Paper>
  );
};

export default StatsCard;

